import axios from 'axios';

// Using Google Maps Geocoding API
const GOOGLE_GEOCODING_BASE_URL =
  'https://maps.googleapis.com/maps/api/geocode/json';
const GOOGLE_PLACES_BASE_URL =
  'https://maps.googleapis.com/maps/api/place/nearbysearch/json';

// Get API key from environment
const getApiKey = () => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    throw new Error(
      'Google Maps API key is required. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file.'
    );
  }
  return apiKey;
};

/**
 * Geocode an address to get latitude/longitude coordinates
 * @param {Object} addressData - Address components
 * @returns {Promise<{lat: number, lng: number, display_name: string}>}
 */
export const geocodeAddress = async (addressData) => {
  try {
    const apiKey = getApiKey();

    // Build the address query
    const { streetAddress, city, state, zipCode } = addressData;
    const address = [
      streetAddress,
      city || 'Los Angeles',
      state || 'CA',
      zipCode,
    ]
      .filter(Boolean)
      .join(', ');

    const response = await axios.get(GOOGLE_GEOCODING_BASE_URL, {
      params: {
        address,
        key: apiKey,
        components: 'country:US', // Restrict to US addresses
      },
    });

    if (response.data.status === 'OK' && response.data.results.length > 0) {
      const result = response.data.results[0];
      const location = result.geometry.location;

      return {
        lat: location.lat,
        lng: location.lng,
        display_name: result.formatted_address,
        place_id: result.place_id,
        address_components: result.address_components,
        types: result.types,
        viewport: result.geometry.viewport,
        bounds: result.geometry.bounds,
      };
    } else if (response.data.status === 'ZERO_RESULTS') {
      throw new Error('No geocoding results found for this address');
    } else if (response.data.status === 'OVER_QUERY_LIMIT') {
      throw new Error('Google Maps API query limit exceeded');
    } else if (response.data.status === 'REQUEST_DENIED') {
      throw new Error('Google Maps API request denied. Check your API key.');
    }

    throw new Error(`Geocoding failed: ${response.data.status}`);
  } catch (error) {
    console.error('Geocoding error:', error);
    if (error.response) {
      throw new Error(
        `Failed to geocode address: ${error.response.data.error_message || error.message}`
      );
    }
    throw new Error(`Failed to geocode address: ${error.message}`);
  }
};

/**
 * Reverse geocode coordinates to get address information
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Object>}
 */
export const reverseGeocode = async (lat, lng) => {
  try {
    const apiKey = getApiKey();

    const response = await axios.get(GOOGLE_GEOCODING_BASE_URL, {
      params: {
        latlng: `${lat},${lng}`,
        key: apiKey,
        result_type: 'street_address|route',
      },
    });

    if (response.data.status === 'OK' && response.data.results.length > 0) {
      const result = response.data.results[0];
      const components = {};

      // Parse address components
      result.address_components.forEach((component) => {
        component.types.forEach((type) => {
          components[type] = component;
        });
      });

      return {
        streetAddress: [
          components.street_number?.long_name,
          components.route?.long_name,
        ]
          .filter(Boolean)
          .join(' '),
        city:
          components.locality?.long_name ||
          components.sublocality?.long_name ||
          components.administrative_area_level_2?.long_name,
        state: components.administrative_area_level_1?.short_name || 'CA',
        zipCode: components.postal_code?.long_name,
        county: components.administrative_area_level_2?.long_name,
        country: components.country?.long_name,
        display_name: result.formatted_address,
        place_id: result.place_id,
      };
    } else if (response.data.status === 'ZERO_RESULTS') {
      throw new Error('No address found at this location');
    }

    throw new Error(`Reverse geocoding failed: ${response.data.status}`);
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    if (error.response) {
      throw new Error(
        `Failed to reverse geocode: ${error.response.data.error_message || error.message}`
      );
    }
    throw new Error(`Failed to reverse geocode: ${error.message}`);
  }
};

/**
 * Search for nearby addresses/places
 * @param {number} lat - Center latitude
 * @param {number} lng - Center longitude
 * @param {number} radius - Search radius in meters
 * @returns {Promise<Array>}
 */
export const searchNearby = async (lat, lng, radius = 100) => {
  try {
    const apiKey = getApiKey();

    // For nearby address search, we'll use reverse geocoding with multiple results
    const response = await axios.get(GOOGLE_GEOCODING_BASE_URL, {
      params: {
        latlng: `${lat},${lng}`,
        key: apiKey,
        result_type: 'street_address',
      },
    });

    if (response.data.status === 'OK') {
      // Filter results by distance
      const results = response.data.results
        .map((result) => {
          const distance = calculateDistance(
            lat,
            lng,
            result.geometry.location.lat,
            result.geometry.location.lng
          );

          return {
            ...result,
            distance,
            lat: result.geometry.location.lat,
            lng: result.geometry.location.lng,
            display_name: result.formatted_address,
          };
        })
        .filter((result) => result.distance <= radius)
        .sort((a, b) => a.distance - b.distance);

      return results;
    }

    return [];
  } catch (error) {
    console.error('Nearby search error:', error);
    throw new Error(`Failed to search nearby: ${error.message}`);
  }
};

/**
 * Validate and format a US address
 * @param {Object} addressData
 * @returns {Object} Formatted address
 */
export const formatAddress = (addressData) => {
  const { streetAddress, city, state, zipCode } = addressData;

  // Basic validation
  if (!streetAddress || !streetAddress.trim()) {
    throw new Error('Street address is required');
  }

  // Format state to uppercase
  const formattedState = (state || 'CA').toUpperCase();

  // Validate state code (basic check)
  if (formattedState.length !== 2) {
    throw new Error('State must be a 2-letter code');
  }

  // Format ZIP code
  let formattedZip = (zipCode || '').replace(/\D/g, '');
  if (formattedZip && formattedZip.length !== 5 && formattedZip.length !== 9) {
    throw new Error('ZIP code must be 5 or 9 digits');
  }
  if (formattedZip.length === 9) {
    formattedZip = `${formattedZip.slice(0, 5)}-${formattedZip.slice(5)}`;
  }

  return {
    streetAddress: streetAddress.trim(),
    city: (city || 'Los Angeles').trim(),
    state: formattedState,
    zipCode: formattedZip,
    fullAddress: [
      streetAddress.trim(),
      (city || 'Los Angeles').trim(),
      `${formattedState} ${formattedZip}`.trim(),
    ]
      .filter(Boolean)
      .join(', '),
  };
};

/**
 * Calculate distance between two coordinates in meters
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number} Distance in meters
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

export default {
  geocodeAddress,
  reverseGeocode,
  searchNearby,
  formatAddress,
  calculateDistance,
};
